import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { Upload, Download, History, CheckCircle2, AlertTriangle, XCircle, Undo2, FileSpreadsheet } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PayrollHistoryImport() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [batches, setBatches] = useState([]);

  useEffect(() => {
    api.get("/companies").then((r) => { setCompanies(r.data); if (r.data[0]) setCompanyId(r.data[0].company_id); }).catch(() => {});
  }, []);

  const loadBatches = useCallback(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/payroll-history/batches`).then((r) => setBatches(r.data)).catch(() => {});
  }, [companyId]);
  useEffect(() => { setPreview(null); setRows([]); setFile(null); loadBatches(); }, [companyId, loadBatches]);

  const downloadTemplate = async (fmt) => {
    try {
      const r = await api.get(`/companies/${companyId}/payroll-history/template.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `payroll_history_template.${fmt}`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const onFile = async (f) => {
    if (!f) return;
    setFile(f); setPreview(null); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/import/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRows(data.rows || []);
      const pv = await api.post(`/companies/${companyId}/payroll-history/preview`, { rows: data.rows || [] });
      setPreview(pv.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!rows.length) return;
    if (!window.confirm(`Import ${preview?.summary?.accepted || 0} pay records as legacy posted payroll history? This affects YTD, T4, ROE and reports.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/payroll-history/commit`, { rows, source_filename: file?.name || "" });
      toast.success(`Imported ${data.batch.payslips_created} pay records into ${data.batch.runs_created} pay runs`);
      setPreview(null); setRows([]); setFile(null); loadBatches();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const undo = async (b) => {
    if (!window.confirm(`Undo this import? Removes ${b.runs_created} pay runs / ${b.payslips_created} records. Blocked if a T4/ROE is already finalized.`)) return;
    try {
      await api.post(`/companies/${companyId}/payroll-history/batches/${b.batch_id}/undo`);
      toast.success("Import undone");
      loadBatches();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const S = preview?.summary || {};
  const T = preview?.totals || {};
  const badge = (st) => st === "accepted"
    ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />OK</span>
    : st === "warning"
      ? <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" />Warning</span>
      : <span className="inline-flex items-center gap-1 text-red-700"><XCircle className="h-3.5 w-3.5" />Rejected</span>;

  return (
    <div className="space-y-6" data-testid="payroll-history-import">
      <div>
        <h2 className="font-bold tracking-tight flex items-center gap-2"><FileSpreadsheet className="h-4 w-4" /> Payroll History Import</h2>
        <p className="text-sm text-muted-foreground mt-1">Bulk-load detailed prior-system pay history (one row per employee per pay date). Imported records are stored as <b>legacy posted payroll</b> and feed YTD, T4, ROE, remittance and reports. Master admin only.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Company</label>
          <select data-testid="phist-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="mt-1 block border border-input bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-black">
            {companies.map((c) => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
          </select>
        </div>
        <button data-testid="phist-template-csv" onClick={() => downloadTemplate("csv")} className="inline-flex items-center gap-2 border border-border px-3 py-2.5 text-xs font-medium hover:border-black transition-colors"><Download className="h-3.5 w-3.5" /> Template (CSV)</button>
        <button data-testid="phist-template-xlsx" onClick={() => downloadTemplate("xlsx")} className="inline-flex items-center gap-2 border border-border px-3 py-2.5 text-xs font-medium hover:border-black transition-colors"><Download className="h-3.5 w-3.5" /> Template (XLSX)</button>
      </div>

      <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border px-6 py-8 cursor-pointer hover:border-black transition-colors" data-testid="phist-dropzone">
        <Upload className="h-6 w-6 text-muted-foreground" />
        <span className="text-sm font-medium">{file ? file.name : "Upload a CSV or XLSX file"}</span>
        <span className="text-[11px] text-muted-foreground">Match the template columns — extra columns are ignored</span>
        <input data-testid="phist-file" type="file" accept=".csv,.xlsx" className="hidden" disabled={!companyId || busy} onChange={(e) => onFile(e.target.files?.[0])} />
      </label>

      {busy && <p className="text-sm text-muted-foreground">Working…</p>}

      {preview && (
        <div className="border border-border" data-testid="phist-preview">
          <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-3 text-xs font-mono uppercase tracking-widest">
            <span className="text-emerald-700" data-testid="phist-accepted">{S.accepted} OK</span>
            <span className="text-amber-700">{S.warning} warning</span>
            <span className="text-red-700" data-testid="phist-rejected">{S.rejected} rejected</span>
            <span className="text-muted-foreground">· {S.runs} pay run{S.runs === 1 ? "" : "s"}</span>
            <span className="ml-auto text-black">Gross {money(T.gross)} · Tax {money(T.income_tax)} · Net {money(T.net_pay)}</span>
          </div>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-neutral-50"><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <th className="px-4 py-2">Row</th><th className="px-4 py-2">Employee</th><th className="px-4 py-2">Pay date</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Notes</th>
              </tr></thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.row} data-testid={`phist-row-${r.row}`} className="border-t border-border">
                    <td className="px-4 py-2 tabular-nums">{r.row}</td>
                    <td className="px-4 py-2">{r.employee_name || r.matched_employee_id || "—"}</td>
                    <td className="px-4 py-2 tabular-nums">{(r.values || {})["pay_date"] || (r.run_key ? r.run_key[2] : "—")}</td>
                    <td className="px-4 py-2">{badge(r.status)}</td>
                    <td className="px-4 py-2 text-muted-foreground">{(r.messages || []).join("; ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-border flex items-center gap-3">
            <button data-testid="phist-commit" disabled={busy || (S.accepted + S.warning) === 0} onClick={commit} className="inline-flex items-center gap-2 bg-black text-white px-4 py-2.5 text-sm font-medium hover:bg-neutral-800 disabled:opacity-40 transition-colors"><History className="h-4 w-4" /> Import {S.accepted + S.warning} records</button>
            {S.rejected > 0 && <span className="text-xs text-red-700">{S.rejected} rejected rows will be skipped</span>}
          </div>
        </div>
      )}

      {batches.length > 0 && (
        <div className="border border-border" data-testid="phist-batches">
          <div className="px-4 py-3 border-b border-border font-mono uppercase tracking-widest text-xs text-muted-foreground">Import history</div>
          <table className="w-full text-xs">
            <tbody>
              {batches.map((b) => (
                <tr key={b.batch_id} data-testid={`phist-batch-${b.batch_id}`} className="border-b border-border last:border-0">
                  <td className="px-4 py-2">{new Date(b.imported_at).toLocaleString("en-CA")}</td>
                  <td className="px-4 py-2 text-muted-foreground">{b.source_filename || "—"}</td>
                  <td className="px-4 py-2 tabular-nums">{b.runs_created} runs · {b.payslips_created} records</td>
                  <td className="px-4 py-2">{b.status === "undone" ? <span className="text-muted-foreground">Undone</span> : <span className="text-emerald-700">Active</span>}</td>
                  <td className="px-4 py-2 text-right">
                    {b.status === "active" && <button data-testid={`phist-undo-${b.batch_id}`} onClick={() => undo(b)} className="inline-flex items-center gap-1.5 border border-border px-2.5 py-1.5 hover:border-red-600 hover:text-red-600 transition-colors"><Undo2 className="h-3.5 w-3.5" /> Undo</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
